import { z } from 'zod';
import superjson from 'superjson';

export const schema = z.object({});

export type AdminUser = {
  id: number;
  email: string;
  displayName: string;
  role: string;
  createdAt: Date;
  sessionCount: number;
  subscriptionTier: string | null;
  subscriptionStatus: string | null;
};

export type AdminSession = {
  id: string;
  name: string;
  status: string;
  ownerId: number | null;
  ownerEmail: string | null;
  ownerDisplayName: string | null;
  templateId: string | null;
  previewSubdomain: string | null;
  fileCount: number;
  participantCount: number;
  createdAt: Date;
  updatedAt: Date;
};

export type AdminSubscription = {
  id: number;
  userId: number;
  userEmail: string;
  userDisplayName: string;
  tier: string;
  status: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: number;
  createdAt: Date | null;
};

export type AdminCollaboration = {
  id: number;
  sessionId: string;
  sessionName: string;
  userId: number;
  userEmail: string;
  userDisplayName: string;
  permissionLevel: string;
  joinedAt: Date;
};

export type AdminPublishedSite = {
  id: number;
  subdomain: string;
  deploymentUrl: string;
  cloudflareProjectName: string;
  sessionId: string;
  userId: number;
  ownerEmail: string | null;
  ownerDisplayName: string | null;
  deploymentStatus: string;
  lastDeployedAt: string | null;
  createdAt: string | null;
};

export type AdminPartner = {
  id: string;
  name: string;
  slug: string;
  contactEmail: string;
  billingEmail: string | null;
  logoUrl: string | null;
  partnerTier: string;
  isActive: number;
  metadata: {
    applicationStatus?: string;
    applicantUserId?: number;
    applicantEmail?: string;
    website?: string | null;
    description?: string | null;
    whyResell?: string | null;
    whyTasking?: string | null;
    companyProfile?: string | null;
    companyProfileName?: string | null;
    agreementId?: string | null;
    signerName?: string | null;
    signerTitle?: string | null;
    signerCompanyName?: string | null;
    signatureImage?: string | null;
    appliedAt?: string;
    approvedAt?: string;
    deniedAt?: string;
    denialReason?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type OutputType = {
  users: AdminUser[];
  sessions: AdminSession[];
  subscriptions: AdminSubscription[];
  collaborations: AdminCollaboration[];
  sites: AdminPublishedSite[];
  partners: AdminPartner[];
  stats: {
    totalUsers: number;
    totalSessions: number;
    activeSessions: number;
    totalSubscriptions: number;
    activeSubscriptions: number;
    trialSubscriptions: number;
    totalSites: number;
    totalPartners: number;
  };
};

export const getTtadminData = async (init?: RequestInit): Promise<OutputType> => {
  const result = await fetch(`/_api/admin/ttadmin`, {
    method: 'GET',
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!result.ok) {
    let errorMsg = 'Failed to load admin data';
    try {
      const errorObject = superjson.parse<{ error: string }>(await result.text());
      errorMsg = errorObject.error;
    } catch {
      try {
        const errorObject = JSON.parse(await result.text());
        errorMsg = errorObject.error || errorMsg;
      } catch {
        /* use default */
      }
    }
    throw new Error(errorMsg);
  }
  return superjson.parse<OutputType>(await result.text());
};
